/**
 * Связи и подзадачи.
 *
 * Иерархия и связи — разные вещи, и сервис держит их порознь: родитель лежит
 * полем самой задачи (`parent_id`), а «блокирует», «связана», «дублирует» —
 * отдельными строками. Экран показывает их рядом, потому что человек читает
 * их одинаково: «с чем ещё связана эта задача».
 */
import { issueApi } from '@/api'
import { toRow, type IssueRow } from '@/features/issues/api'

import type { V1IssueLink, V1LinkType } from '@/api/clients/tracker-issue'

/** Порядок в выпадашке: сначала то, что ставят чаще */
export const LINK_TYPES: V1LinkType[] = [
  'LINK_TYPE_BLOCKS',
  'LINK_TYPE_RELATES',
  'LINK_TYPE_DUPLICATES',
  'LINK_TYPE_CAUSES',
]

export interface IssueLink {
  id: string
  type: V1LinkType | ''
  targetKey: string
  targetTitle: string
  /** Задача на другом конце связи: строка читается наоборот */
  inbound: boolean
}

const toLink = (link: V1IssueLink): IssueLink => ({
  id: link.id ?? '',
  type: link.linkType ?? '',
  targetKey: link.targetKey ?? '',
  targetTitle: link.targetTitle ?? '',
  inbound: Boolean(link.inbound),
})

export const listLinks = async (issueId: string): Promise<IssueLink[]> => {
  const { data } = await issueApi.issueServiceListIssueLinks(issueId)

  return (data.links ?? []).map(toLink)
}

export const createLink = async (
  issueId: string,
  targetIssueId: string,
  linkType: V1LinkType,
): Promise<void> => {
  await issueApi.issueServiceCreateIssueLink(issueId, { targetIssueId, linkType })
}

export const deleteLink = async (id: string): Promise<void> => {
  await issueApi.issueServiceDeleteIssueLink(id)
}

/**
 * Подзадачи — это задачи с этим родителем, и берёт их обычный листинг: своей
 * ручки «дети такой-то задачи» у сервиса нет, а фильтр по родителю есть.
 */
export const listChildren = async (projectId: string, parentId: string): Promise<IssueRow[]> => {
  const { data } = await issueApi.issueServiceListIssues(
    projectId,
    undefined,
    undefined,
    undefined,
    undefined,
    undefined,
    undefined,
    parentId,
    undefined,
    undefined,
    undefined, // filterProjectIds
    undefined, // filterStatusCategories
    undefined, // filterDueAfter
    undefined, // filterAncestorId — один уровень, поддерево берёт listSubtree
    'key',
    false,
    '1',
    // Сотня подзадач у одной задачи — уже не иерархия, а отдельный проект:
    // страницы здесь были бы дороже, чем пользы от них.
    '100',
  )

  return (data.issues ?? []).map(toRow)
}

/** Размер страницы поддерева. Больше сервис всё равно не отдаёт. */
const SUBTREE_PAGE = 100

/**
 * Всё, что лежит под задачей, на любой глубине.
 *
 * Дерево обходит база — фильтром `ancestorId`. Раньше его обходил экран: брал
 * детей, а потом спрашивал подзадачи каждого ребёнка отдельным запросом. У
 * эпика в сотню историй это сотня запросов разом, и они выбирали лимит
 * проверки прав целиком: карточка показывала «0 задач» там, где их сто
 * семьдесят (DEV-1424).
 *
 * Страницы дочитываются до конца: в эпике бывает больше сотни задач, а
 * оборванная на первой странице выдача врёт молча — прогресс считается по
 * тому, что доехало.
 */
export const listSubtree = async (projectId: string, ancestorId: string): Promise<IssueRow[]> => {
  const issues: IssueRow[] = []

  for (let page = 1; ; page += 1) {
    const { data } = await issueApi.issueServiceListIssues(
      projectId,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined, // filterParentId — нужен не уровень, а всё поддерево
      undefined,
      undefined,
      undefined, // filterProjectIds
      undefined, // filterStatusCategories
      undefined, // filterDueAfter
      ancestorId,
      'key',
      false,
      String(page),
      String(SUBTREE_PAGE),
    )

    issues.push(...(data.issues ?? []).map(toRow))

    // Счётчик приходит строкой: в protobuf это uint64, в number он не влезает.
    if (issues.length >= Number(data.pageInfo?.total ?? 0)) return issues

    // Пустая страница при недобранном итоге — повод остановиться, а не крутить
    // цикл: значит выдача разошлась со счётчиком, и следующий заход вернёт то
    // же самое.
    if (!data.issues?.length) return issues
  }
}
